import type { AudioTestModalProps } from './index.types';
import React, { useCallback, useState } from 'react';
import {
  comprehensiveAudioDeviceTest,
  formatAudioDeviceTestResults,
} from 'Utils/diagnostics/audioDeviceTest';
import { getLogger } from 'Utils/logger';
import { DiagnosticsModal } from '../DiagnosticsModal';

export const AudioTestModal: React.FC<AudioTestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string | null>(null);

  const runTest = useCallback(async () => {
    setIsRunning(true);
    setResults(null);

    try {
      const testResults = await comprehensiveAudioDeviceTest();
      const formattedResults = formatAudioDeviceTestResults(testResults);
      setResults(formattedResults);

      // Send results to log collector
      const logger = getLogger();
      logger.info('Audio Device Test Completed', {
        testResults,
        formattedResults,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      });
    } catch (error) {
      const errorMessage = `Error running audio device test: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      setResults(errorMessage);

      // Log error
      const logger = getLogger();
      logger.error('Audio Device Test Failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
    } finally {
      setIsRunning(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    setResults(null);
    onClose();
  }, [onClose]);

  return (
    <DiagnosticsModal
      title={'Audio Device Diagnostics'}
      description={
        'This test checks microphone access, speaker enumeration, and audio device functionality. It will help diagnose issues with audio devices including the "AbortError" that can occur when setting speaker devices.'
      }
      placeholder={
        <div>
          <strong>What this test does:</strong>
          <ul>
            <li>Tests microphone access and permissions</li>
            <li>Enumerates available audio input/output devices</li>
            <li>Tests speaker selection (setSinkId) functionality</li>
            <li>Detects AbortError and other audio device errors</li>
            <li>Sends results to support team automatically</li>
          </ul>
        </div>
      }
      isOpen={isOpen}
      isRunning={isRunning}
      results={results}
      runTest={() => void runTest()}
      onClose={handleClose}
    />
  );
};
